import React from 'react';
import DataGridMultilinePopper from '@/components/table/DataGridComponents/DataGridMultilinePopper';
import { isProspectFieldVisible } from '@/features/config/helper';
import { FormFieldConfigSwitchCell } from './FormFieldConfigSwitchCell';
import { FormFieldConfigOwnerCell } from './FormFieldConfigOwnerCell';
import { FormFieldConfigSharedWithCell } from './FormFieldConfigSharedWithCell';
import { FormFieldConfigColumnHeader } from './FormFieldConfigColumnHeader';

// Headers as Settings → Client config shows them; headerName stays the one-line name the
// Columns panel lists
const header = (title, tooltip, subtitle) =>
	function renderHeader() {
		return <FormFieldConfigColumnHeader title={title} subtitle={subtitle} tooltip={tooltip} />;
	};

const switchColumn = {
	width: 120,
	sortable: false,
	display: 'flex',
};

const textColumn = {
	flex: 2,
	minWidth: 200,
	sortable: false,
	editable: true,
	renderEditCell: (params) => <DataGridMultilinePopper {...params} newLine={false} />,
};

/**
 * Columns for the admin form field config list. The same settings and rules as Settings →
 * Client config (making a field required makes it visible; a field required by default can
 * can't be made not required unless its `optional` allows it), plus Description, which only
 * admins change.
 */
export const formFieldConfigColumns = [
	{
		field: 'label',
		headerName: 'Label',
		renderHeader: header('Label', 'The label used in the Create Client form'),
		flex: 1.5,
		minWidth: 180,
		editable: true,
		hideable: false,
	},
	{
		field: 'name',
		headerName: 'Field Name',
		renderHeader: header('Field Name', 'The internal name of the field in Insight or Firebase'),
		flex: 1.5,
		minWidth: 180,
	},
	{
		...switchColumn,
		field: 'mandatory.client',
		headerName: 'Required (client)',
		renderHeader: header(
			'Required',
			'Whether or not this field is mandatory for clients. Some fields are required by Insight as a default and cannot be changed.',
			'Client',
		),
		valueGetter: (value, row) => row.mandatory?.client,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='required for clients'
				checked={row.mandatory?.client}
				disabled={row.mandatory?.client && !row.optional}
				disabledReason='This field is required by default and cannot be changed'
				getValues={(checked) => ({
					mandatory: { ...row.mandatory, client: checked },
					...(checked && { visible: { ...row.visible, create: true } }),
				})}
			/>
		),
	},
	{
		...switchColumn,
		field: 'mandatory.prospect',
		headerName: 'Required (prospect)',
		renderHeader: header(
			'Required',
			'Whether or not this field is mandatory for prospects. Some fields are required by Insight as a default and cannot be changed.',
			'Prospect',
		),
		valueGetter: (value, row) => row.mandatory?.prospect,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='required for prospects'
				checked={row.mandatory?.prospect}
				disabled={row.mandatory?.prospect && !row.optional}
				disabledReason='This field is required by default and cannot be changed'
				getValues={(checked) => ({
					mandatory: { ...row.mandatory, prospect: checked },
					...(checked && {
						visible: { ...row.visible, prospect: { create: true, update: true } },
					}),
				})}
			/>
		),
	},
	{
		...switchColumn,
		field: 'visible.create',
		headerName: 'Visible (create)',
		renderHeader: header(
			'Visible',
			'Whether this field is visible in the Create Client form',
			'Create client',
		),
		valueGetter: (value, row) => row.visible?.create,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='visible when creating a client'
				checked={row.visible?.create}
				disabled={row.mandatory?.client}
				disabledReason='Required for clients, so always visible'
				getValues={(checked) => ({ visible: { ...row.visible, create: checked } })}
			/>
		),
	},
	// Prospect visibility is saved per view ({ create, update }); isProspectFieldVisible also
	// reads the older single boolean
	...['create', 'update'].map((view) => ({
		...switchColumn,
		field: `visible.prospect.${view}`,
		headerName: `Visible (${view} prospect)`,
		renderHeader: header(
			'Visible',
			`Whether this field is visible in the ${view === 'create' ? 'Create' : 'Update'} Client form for prospects`,
			`${view === 'create' ? 'Create' : 'Update'} prospect`,
		),
		valueGetter: (value, row) => isProspectFieldVisible(row, view),
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label={`visible when ${view === 'create' ? 'creating' : 'updating'} a prospect`}
				checked={isProspectFieldVisible(row, view)}
				disabled={row.mandatory?.prospect}
				disabledReason='Required for prospects, so always visible'
				getValues={(checked) => ({
					visible: {
						...row.visible,
						prospect: {
							create: isProspectFieldVisible(row, 'create'),
							update: isProspectFieldVisible(row, 'update'),
							[view]: checked,
						},
					},
				})}
			/>
		),
	})),
	{
		...switchColumn,
		field: 'visible.update',
		headerName: 'Visible (update)',
		renderHeader: header(
			'Visible',
			'Whether this field is visible in the Client Details section',
			'Update client',
		),
		valueGetter: (value, row) => row.visible?.update,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='visible when updating a client'
				checked={row.visible?.update}
				getValues={(checked) => ({ visible: { ...row.visible, update: checked } })}
			/>
		),
	},
	{
		...textColumn,
		field: 'helperText',
		headerName: 'Helper Text',
		renderHeader: header(
			'Helper Text',
			'The helper text the user sees; leave blank to not display',
		),
	},
	{
		...textColumn,
		field: 'description',
		headerName: 'Description',
		renderHeader: header('Description', "Shown when hovering over the field's label and name"),
	},
	// Left out on the default tab (FormFieldConfigsTable)
	{
		field: 'scope',
		headerName: 'Owner',
		width: 200,
		display: 'flex',
		// Alphabetical by group name or ledger code; the default has none, so sorts to one end
		valueGetter: (value, row) => row.ownerName,
		renderHeader: header(
			'Owner',
			"Whose settings this row has: a broker group, a ledger or the default. A shared row's owner is the group it's shared from; break the link to edit it here.",
		),
		renderCell: ({ row }) => <FormFieldConfigOwnerCell config={row} />,
	},
	// Only on the broker group tab (FormFieldConfigsTable): only broker group configs are shared
	{
		field: 'sharedWith',
		headerName: 'Shared with',
		flex: 1.5,
		minWidth: 200,
		sortable: false,
		display: 'flex',
		renderHeader: header(
			'Shared with',
			'The broker groups this row is shared with. Changes here change theirs too.',
		),
		renderCell: ({ row }) => <FormFieldConfigSharedWithCell config={row} />,
	},
];
